import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { Dialog, RadioGroupItem } from 'components';
import { useInfluenzaVaccines } from 'hooks/useInfluenzaVaccines';
import type { ImmunizationDraft, VaccinationReason } from 'types/immunization';
import {
  BatchField,
  BatchHint,
  BatchInput,
  BatchLabel,
  CancelButton,
  EmptyState,
  FieldError,
  FieldErrorIcon,
  FieldGroup,
  FieldLegend,
  OptionLabel,
  OptionList,
  OptionRow,
  PatientLine,
  SubmitButton,
  VaccinationDialogContent,
  VaccinationDialogFooter,
  VaccinationDialogHeader,
  VaccinationDialogTitle,
  VaccineOption,
} from './VaccinationDialog.styles';

interface VaccinationDialogProps {
  availableReasons: VaccinationReason[];
  patientLine?: string;
  /** False keeps the dialog open; the caller has already told the user why. */
  onSubmit: (draft: ImmunizationDraft) => Promise<boolean> | boolean;
  /** Where the focus goes when the dialog closes; its trigger row disappears with the alert. */
  focusAfterClose?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

const defaultReason = (availableReasons: VaccinationReason[]): VaccinationReason | null => {
  if (availableReasons.includes('medical')) return 'medical';
  return availableReasons[0] ?? null;
};

export const VaccinationDialog = ({
  availableReasons,
  patientLine,
  onSubmit,
  focusAfterClose,
  onClose,
}: VaccinationDialogProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const idPrefix = useId();
  const { data, isLoading, isError } = useInfluenzaVaccines(true);
  const vaccines = data?.vaccines ?? [];

  const [vaccineName, setVaccineName] = useState<string | null>(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [reason, setReason] = useState<VaccinationReason | null>(defaultReason(availableReasons));
  const [showVaccineError, setShowVaccineError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const suggestedBatch = vaccines.find((vaccine) => vaccine.name === vaccineName)?.lastBatchNumber;

  const selectVaccine = (name: string) => {
    setVaccineName(name);
    setBatchNumber(vaccines.find((vaccine) => vaccine.name === name)?.lastBatchNumber ?? '');
    setShowVaccineError(false);
  };

  const finish = () => {
    toast.success(t('result.immunization.added'));
    trackEvent(ANALYTICS_EVENTS.IMMUNIZATION_ADDED);
    onClose();
  };

  const submit = () => {
    if (!vaccineName || !data) {
      setShowVaccineError(true);
      return;
    }
    const { diagnosis } = data;
    const draft: ImmunizationDraft = {
      vaccineName,
      batchNumber: batchNumber.trim(),
      reason,
      diagnosisSelected: diagnosis.selected,
      diagnosisCode: diagnosis.code,
      diagnosisTitle: diagnosis.title,
      diagnosisCategory: diagnosis.category,
      diagnosisSicherheit: diagnosis.sicherheit,
      diagnosisSeite: diagnosis.seite,
      availableReasons,
    };
    setSubmitting(true);
    void Promise.resolve(onSubmit(draft)).then((ok) => {
      setSubmitting(false);
      if (ok) finish();
    });
  };

  const showEmptyState = !isLoading && !isError && vaccines.length === 0;
  const canSubmit = !isLoading && !isError && !showEmptyState && !submitting;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <VaccinationDialogContent
        data-ph-no-autocapture
        onCloseAutoFocus={(event) => {
          const target = focusAfterClose?.current;
          if (!target) return;
          event.preventDefault();
          target.focus();
        }}
      >
        <VaccinationDialogHeader>
          <VaccinationDialogTitle>{t('result.immunization.dialogTitle')}</VaccinationDialogTitle>
          <PatientLine $visuallyHidden={!patientLine}>
            {patientLine ?? t('result.immunization.dialogTitle')}
          </PatientLine>
        </VaccinationDialogHeader>

        <FieldGroup aria-labelledby={`${idPrefix}-vaccines-label`}>
          <FieldLegend id={`${idPrefix}-vaccines-label`}>
            {t('result.immunization.vaccineLabel')}
          </FieldLegend>
          {isError ? (
            <EmptyState role="alert">{t('result.immunization.loadFailed')}</EmptyState>
          ) : showEmptyState ? (
            <EmptyState>{t('result.immunization.empty')}</EmptyState>
          ) : (
            <OptionList
              value={vaccineName ?? ''}
              onValueChange={selectVaccine}
              aria-labelledby={`${idPrefix}-vaccines-label`}
            >
              {vaccines.map((vaccine, index) => {
                const optionId = `${idPrefix}-vaccine-${index}`;
                const selected = vaccine.name === vaccineName;
                return (
                  <VaccineOption key={vaccine.name}>
                    <OptionRow>
                      <RadioGroupItem value={vaccine.name} id={optionId} />
                      <OptionLabel htmlFor={optionId}>{vaccine.name}</OptionLabel>
                    </OptionRow>
                    {selected && (
                      <BatchField>
                        <BatchLabel htmlFor={`${idPrefix}-batch`}>
                          {t('result.immunization.batchLabel')}
                        </BatchLabel>
                        <BatchInput
                          id={`${idPrefix}-batch`}
                          value={batchNumber}
                          maxLength={64}
                          onChange={(event) => setBatchNumber(event.target.value)}
                        />
                        {suggestedBatch && batchNumber === suggestedBatch && (
                          <BatchHint>{t('result.immunization.lastUsed')}</BatchHint>
                        )}
                      </BatchField>
                    )}
                  </VaccineOption>
                );
              })}
            </OptionList>
          )}
          {showVaccineError && (
            <FieldError>
              <FieldErrorIcon />
              {t('result.immunization.vaccineRequired')}
            </FieldError>
          )}
        </FieldGroup>

        {availableReasons.length > 0 && (
          <FieldGroup aria-labelledby={`${idPrefix}-reasons-label`}>
            <FieldLegend id={`${idPrefix}-reasons-label`}>
              {t('result.immunization.reasonLabel')}
            </FieldLegend>
            <OptionList
              value={reason ?? ''}
              onValueChange={(value) => setReason(value as VaccinationReason)}
              aria-labelledby={`${idPrefix}-reasons-label`}
            >
              {availableReasons.map((option) => {
                const optionId = `${idPrefix}-reason-${option}`;
                return (
                  <OptionRow key={option}>
                    <RadioGroupItem value={option} id={optionId} />
                    <OptionLabel htmlFor={optionId}>
                      {t(`result.immunization.reasons.${option}`)}
                    </OptionLabel>
                  </OptionRow>
                );
              })}
            </OptionList>
          </FieldGroup>
        )}

        <VaccinationDialogFooter>
          <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
          <SubmitButton onClick={submit} disabled={!canSubmit}>
            {t('result.immunization.submit')}
          </SubmitButton>
        </VaccinationDialogFooter>
      </VaccinationDialogContent>
    </Dialog>
  );
};
